import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { AIOutcome } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import { CallStatus as PhoneCallStatus } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import { CallIntelTypes, CallIntelUtils } from '@frontend/api-call-intel';
import { PhoneCallsTypes } from '@frontend/api-phone-calls';
import { getDisplayName } from '@frontend/get-display-name';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { ChipVariants } from '@frontend/design-system';

const normalizeCallStatus = (status?: string) => String(status || '').toLowerCase();

export const isMissedCallStatus = (status?: string) =>
  status === PhoneCallStatus.CALL_STATUS_MISSED || normalizeCallStatus(status) === 'missed';

export const isAbandonedCallStatus = (status?: string) =>
  status === PhoneCallStatus.CALL_STATUS_ABANDONED || normalizeCallStatus(status) === 'abandoned';

export const isMissedOrAbandonedCallStatus = (status?: string) =>
  isMissedCallStatus(status) || isAbandonedCallStatus(status);

export const shouldHideConversationForCallStatus = ({
  status,
  aiOutcome,
}: {
  status?: string;
  aiOutcome?: AIOutcome;
}) => {
  const isMissedCall = isMissedCallStatus(status);
  const isAbandonedCall = isAbandonedCallStatus(status);
  const isMissedCallHandledByAi = isMissedCall && !!aiOutcome && aiOutcome !== AIOutcome.AI_OUTCOME_UNKNOWN;

  return isAbandonedCall || (isMissedCall && !isMissedCallHandledByAi);
};

/**
 * @deprecated - Please use `getFullName` from `@frontend/get-display-name` instead.
 */
export const getFullName = (firstName?: string, lastName?: string, defaultValue = '') => {
  if (firstName || lastName) {
    return `${firstName || ''} ${lastName || ''}`.trim();
  }
  return defaultValue || '';
};

export const getOfficeUser = (callInfo: HydratedCallRecord, showEmptyValue: string) => {
  const { user, status, direction, sipName } = callInfo;
  // if it is an inbound call with status abandoned or missed, then show `showEmptyValue`, otherwise use sipName
  const defaultValue =
    direction === 'inbound' && isMissedOrAbandonedCallStatus(status) ? showEmptyValue : sipName || showEmptyValue;
  const fullName = getFullName(user?.firstName, user?.lastName, defaultValue);
  return fullName;
};

export const getStatusChipColor = (status: PhoneCallsTypes.CallLog['Status']): ChipVariants => {
  switch (status) {
    case 'abandoned':
      return 'warn';
    case 'missed':
      return 'critical';
    case 'answered':
      return 'primary';
    case 'forwarded':
      return 'eggplant';
    default:
      return 'outline';
  }
};

export const getContactInfo = (call: HydratedCallRecord) => {
  const locationId = call?.locationId ?? '';
  const personId = call?.person?.personId ?? '';
  const originalfirstName = call?.person?.firstName ?? '';
  const originalLastName = call?.person?.lastName ?? '';
  const preferredName = call?.person?.preferredName ?? '';
  const {
    firstName,
    lastName,
    fullName: profileName,
    legalName,
  } = getDisplayName({
    firstName: originalfirstName,
    lastName: originalLastName,
    preferredName,
  });

  const number = formatPhoneNumber(call?.direction === 'inbound' ? call?.callerNumber : call?.dialedNumber);
  return { firstName, lastName, locationId, number, personId, profileName, legalName };
};

export const processTasks = (tasks: CallIntelTypes.Task[] | undefined, transcript: string): CallIntelTypes.Task[] => {
  if (!tasks) return [];

  return tasks.map((task) => {
    let parsedMetadata;
    let citation = '';
    if (task.metadata) {
      try {
        parsedMetadata = JSON.parse(task.metadata);
        citation = parsedMetadata.citation || '';
      } catch (error) {
        console.error('Error parsing metadata:', error);
      }
    }
    return {
      ...task,
      citation,
      citationTime: CallIntelUtils.findCitationTime(transcript, citation),
    } as CallIntelTypes.Task;
  });
};
